const reports = {
  backTo: "Back to financial reports",
  financialReports: {
    title: "Financial reports",
    content1:
      "Get a clear view of your account activity and balance, and seamlessly allocate revenue to individual practitioners if needed.",
    content2:
      "By default, only the account owner can see reports. You can grant access to your office admin or other practitioners through<1/><2>report access settings.</2>",
    past30Days: "Past 30 days",
    pastYear: "Past year",
    allTime: "All time",
    footnote: "*All amounts are based on MSRP",
  },
  payoutReports: {
    title: "Payout Reports",
    content:
      "View current and past payout reports to see which shipments and refunds were processed for any payout deposited to your bank account.",
    button: "View payout reports",
    subtitle: "Payout reports",
    currentBalance: "Current balance",
    nextPayoutDate: "Next payout date",
    bankAccount: "Bank account",
    payout: "Payout",
    available: "Available",
    statementNumber: "Statement number:",
    depositTotal: "Deposit total:",
    downloadAsCSV: "Download as CSV",
    summary: "Summary",
    subtotal: "Subtotal",
    taxes: "Taxes",
    totalEarnings: "Total earnings",
    earnings: "Earnings",
    returns: "Returns",
    canceledOrders: "Canceled orders",
    total: "Total",
    bonus: "(includes {{bonusProceeds}} bonus)",
    tableHeaders: {
      order: "Order",
      shipment: "Shipment",
      date: "Date",
      patient: "{{patientTerm}}",
      msrp: "MSRP",
      discount: "Discount",
      sellingPrice: "Selling price",
      cogs: "COGS†",
      netProceeds: "Net Proceeds",
    },
    footer:
      "(†) Included in Cost of Goods is a fee of 10% of MSRP for administration, handling, and payment processing services provided by Fullscript to your {{patientTerm}} on your behalf.",
    bonusFooter: "(‡) Bonus proceeds can be accrued from the Fullscript Referral Program.",
  },
  accountActivityAndBalanceReports: {
    title: "Account activity and balance reports",
    content:
      "Provides a complete history of shipments, refunds, and processed payouts with a running balance of your account standing. This offers the necessary detail to understand your current account balance.",
    link: "How do I use this report?",
    netIncome: "Net income",
    netSales: "Net sales",
  },
  detailedEarningsReport: {
    title: "Detailed earnings report",
    content:
      "A per-line export of items shipped with retail price, discounted price, and the {{treatmentPlanTerm}} practitioner.",
    search: "Search...",
  },
  exportReports: {
    title: "1099-K exports",
    content: "A per order export of 1099-K transactions and their associated adjustments.",
    error: "Download unavailable",
  },
  eDelivery: {
    title: "Form 1099-K delivery method",
    content: "Select how you want to receive your Form 1099-K",
    downloadContent: "Your 1099-K form can be downloaded here.",
    selectDeliveryMethod: "Select delivery method",
    disclosureInformation: {
      title: "Disclosure information",
      agree: "I agree to receiving my Form 1099-K electronically",
      point1:
        "﻿If you do not wish to consent to electronic delivery, you will receive a mailed copy.",
      point2:
        "Your consent to electronic delivery will apply to all future 1099-K forms delivered by Stripe unless you withdraw consent (see point 4 below).",
      point3:
        "If for any reason you would like a paper copy of your Form 1099-K after consenting to paperless delivery, you may submit a request to <2>{{supportEmail}}</2>. Requesting a paper copy of your Form 1099-K will not be treated as a withdrawal of consent.",
      point4:
        "If you would like to withdraw your consent to electronic delivery, you may withdraw consent on the Financial Reports page. Once you have withdrawn consent, we will confirm the withdrawal and its effective date in writing. A withdrawal of consent does not apply to a Form 1099-K that was electronically delivered to you before the effective date of the withdrawal of consent.",
      point5:
        "Fullscript will cease providing tax forms to you electronically if you withdraw consent.",
      point6:
        "If the information on your tax form is incorrect, please reach out to <2>{{supportEmail}}</2>",
      point7:
        "Forms will be provided in PDF format, Fullscript may ask you to provide consent again if the format changes.",
    },
    changeToPhysicalMail: "Change delivery method to physical mail",
    changeToEDelivery: "Change delivery method to e-delivery",
    eDelivery: "E-delivery",
    physicalMail: "Mail",
    confirmChange: "Confirm change",
    eDeliveryMethodSelected: "E-delivery method selected",
    deliveryMethodChanged: "1099-K delivery method changed",
    changeMethodToPhysicalHeader: "Change 1099-K delivery method to physical mail?",
    changeMethodToPhysicalContent:
      "By opting for a mailed copy for your delivery method, you'll withdraw your consent for electronic delivery. Fullscript will stop sending tax forms electronically, and you'll only receive printed form(s) via mail.",
    changeMethodToPhysicalContent2:
      "Note that this withdrawal of consent doesn't affect Form 1099-K's already delivered electronically before the withdrawal date.",
    youCanChange:
      "You can change your delivery preferences in your <2>financial preferences page</2>.",
    importantDetails:
      "<0>Important:</0> If you need to make changes to your tax details, please remember to email a completed <3>W9 form</3> to <6>tax-compliance@fullscript.com</6>",
    electronicDelivery:
      "I want to have access to my Form 1099-K in my financial report to download anytime.",
    mailDelivery:
      "I prefer receiving my Form 1099-K through mail only. The form will be mailed on the first week of january and can take a few weeks to arrive.",
    disclaimer:
      "* By choosing this option, you will not have access to a digital copy of Form 1099-K",
  },
  practitionerEarningsReport: {
    title: "Practitioner earnings report",
    content:
      "This export is most useful for clinics with multiple Fullscript practitioners to allocate revenue to individual practitioners.",
    button: "Create report",
    create: {
      title: "Create a practitioner earnings report",
      titleMobile: "Earnings report",
      stepper: {
        step1: "Compensation model",
        step2: "Payout periods",
        step3: "Review report",
      },
      step1: {
        title: "Select your clinic's compensation model",
        titleMobile: "Select compensation model",
        option1: {
          title: "{{patientTermCapitalize}} owner compensated",
          description:
            "Compensate practitioners for all products purchased by their {{patientTermPlural}}.",
          bulletPointsTitle: "How it works:",
          bulletPoints: `
          <0>Assign a {{patientTerm}} owner: Designate a default payee for orders made by each {{patientTerm}}.</0>
          <1>Easy payee override: If needed, you can change the payee for an order without altering the {{patientTerm}} owner.</1>
          <2>Single payee per order: Each order will have a clear and singular payee, simplifying the compensation process.</2>
          `,
        },
        option2: {
          title: "Plan author compensated",
          description:
            "Compensate practitioners for recommended products purchased by {{patientTermPlural}}.",
          bulletPointsTitle: "How it works:",
          bulletPoints: `
          <0>If multiple practitioners recommend the same product to a {{patientTerm}}, the practitioner with the latest plan will receive compensation for that product.</0>
          <1>If a {{patientTerm}} orders products without any practitioner plan, those items will be listed as "Unassigned" in the report export, and no compensation will be attributed.</1>
          `,
        },
        ctaLabel: "Next: Select earnings periods",
      },
      step2: {
        title: "Select payout period(s)",
        prefixText: "Year",
        createEarningsReport: "Create earnings report",
        downloadEarningsReport: "Download earnings report",
        emptyStateHeader: "No payout periods available",
        emptyStateBody:
          "Payout period options will appear as you receive orders from {{patientTermPlural}}.",
        selectPayoutPeriods: "Select payout period(s)",
        compensationModel: "Compensation model:",
        changeCompensationModel: "Change compensation model",
      },
      step3: {
        title: "Review payees and download earnings report",
        subtitle:
          "All orders in the selected payouts and all future orders by that {{patientTerm}} will have the selected practitioner as their default payee. The earnings report will be downloaded as an Excel file.",
        prefixText: "Practitioner",
        sku: "SKU",
        prescriber: "Prescriber",
        quantity: "Quantity",
        assignOrder: "Assign order",
        assignAllOrders: "Assign all orders",
        practitioners: "Prescribing practitioners",
        clinic: "Clinic",
        others: "Others",
        bonusProceeds: "Bonus proceeds:",
        downloadEarningsReport: "Download earnings report",
        toggleOrderDetails: "Toggle order details",
        date: "Date",
        amount: "Amount",
        returns: "Returns",
        cancellations: "Cancellations",
        successToast: "Order assigned successfully",
        successToastPlural: "Orders assigned successfully",
        paidInSeparatePeriod: "Paid in separate period",
        downloadWarning:
          "Not all orders have been assigned a payee. The earnings on any order without a payee will remain unassigned.",
        noneSelected: "None selected",
        noPayee: "No payee",
        assignedToFutureOrders:
          "{{-payeeName}} will be assigned to {{patientName}}'s future orders.",
        massAssignMessage:
          " Make payee the patient owner? They will become the default payee for future orders by {{patientName}}.",
        massAssignCta: "Yes",
        singleAssignCta: "No, just this order",
        showHideDetails: "Show/hide details",
        choosePayee: "Choose payee",
      },
    },
    downloadSucessMessage: "Earnings report downloaded successfully.",
  },
  payoutYear: "Year",
  search: "Search...",
  download: "Download",
  downloadAs: "Download as",
} as const;

export { reports };
